import { Button } from '@mui/material';
import CustomTooltip from '__common/components/CustomTooltip';
import React from 'react';
import SaveChangesButton from '__common/components/Drawer/components/SaveChangesButton';
import { connect } from 'react-redux';
import { dispatch } from '../../store';
import { footerHeight } from '__common/constants/layout_css_dims';
import { disabled722BuildingCodeProducts, isRMFamily } from '__common/constants/products';
import { OPEN_DRAWER_PAGE } from 'actions';
import { isModuleSelectorValid } from '../moduleSelector/moduleSelectorHelper';
import { asce_722 } from '__common/constants/buildingCodes';

type Props = {
  dispatch: Function,
  pages: PAGE[];
  page: string | null;
  mainClass?: string;
  listClass?: string;
  legacy?: boolean;
  drawerHeight?: number;
  saveButton?: boolean;
  saveButtonLabel?: string,
  keepOpen?: boolean;
  inactive?: boolean;
  productId: number,
  shouldConfigureFirst?: boolean,
  onSave?: Function,
  projectConfigurated: boolean,
  moduleSelector: moduleSelectorState,
  verifiedFlyouts?: verifiedFlyouts,
  building_code?: number
};

type PAGE = {
  page: string;
  icon: any;
  content: JSX.Element;
  active?: boolean;
  keepOpen?: boolean;
  postRender?: Function;
  disabled?: boolean,
  tooltip: any;
  invalid?: boolean;
  bottomIcon?: boolean;
  onClick?: Function;
};

type State = {
  configuredPages: string[],
  pageInConfiguration: boolean,
};

class Drawer extends React.Component<Props, State> {
  state = {
    configuredPages: [],
    pageInConfiguration: false,
  };

  drawerList: any;
  drawerContent: any;

  contentHeight = this.props.drawerHeight
    ? this.props.drawerHeight
    : `calc(100vh - ${3 * footerHeight}px)`;

  componentDidMount() {
    const { page } = this.props;

    this.openActive();

    if (page === null) {
      this.setPageOpenIfShouldBe();
    }

    document.addEventListener('mousedown', this.handleClickOutside);
    document.addEventListener('keydown', this.handleKeyDown);
    
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  componentDidUpdate(prevProps: Props) {
    const { page, shouldConfigureFirst } = this.props;

    this.setPageOpenIfShouldBe();

    if (shouldConfigureFirst) {
      this.forceToConfigurePages();
    }

    if (!page) {
      return;
    }

    if (prevProps.drawerHeight !== this.props.drawerHeight) {
      this.contentHeight = this.props.drawerHeight
        ? this.props.drawerHeight
        : `calc(100vh - ${footerHeight}px)`;
    }

    const postRenderPage = this.props.pages.find(p => p.page === page);
    let postRender: Function | undefined;

    if (postRenderPage) {
      postRender = postRenderPage.postRender;
    }

    if (postRender) {
      postRender();
    }

    if (prevProps.productId !== this.props.productId) {
      this.openActive();
    }

  }

  forceToConfigurePages() {
    const { configuredPages, pageInConfiguration } = this.state;
    const { pages, projectConfigurated, shouldConfigureFirst } = this.props;

    if (pageInConfiguration || projectConfigurated && shouldConfigureFirst) {
      return;
    }

    pages.some(p => {
      const pa = p.page;
      if (this.isConfigurationPage(p) && !configuredPages.includes(pa)) {
        dispatch(OPEN_DRAWER_PAGE(pa));
        this.setState({ pageInConfiguration: true });
        return true;
      }
    });
  }

  getPage = (page: string) => {
    return this.props.pages.find(p => p.page === page);
  }

  isContentTab = (page: PAGE) => Boolean(page.content) && !page.onClick;

  isConfigurationPage = (page: PAGE) => (
    this.isContentTab(page) && !page.bottomIcon && !page.disabled
  );

  getNextConfigurationPage = (configuredPages: string[]) => {
    const currentIndex = this.props.pages.findIndex(p => p.page === this.props.page);
    const remainingPages = currentIndex >= 0
      ? [
          ...this.props.pages.slice(currentIndex + 1),
          ...this.props.pages.slice(0, currentIndex),
        ]
      : this.props.pages;

    return remainingPages.find(page => (
      this.isConfigurationPage(page) && !configuredPages.includes(page.page)
    ));
  }

  setPageOpenIfShouldBe = () => {
    const { dispatch, page, pages } = this.props;
    
    if (!page) {
      return;
    }
    
    const pageExists = pages.some(p => p.page === page && !p.disabled);

    if (!pageExists) {
      const fallbackPage = pages.find(p => p.active && !p.disabled) || pages.find(p => !p.disabled);
      if (fallbackPage) {
        dispatch(OPEN_DRAWER_PAGE(fallbackPage.page));
      }
      return;
    }

    pages.some(p => {
      if (p.invalid && p.page !== page) {
        dispatch(OPEN_DRAWER_PAGE(p.page));
        return true;
      }

      if (p.keepOpen && p.page === page) {
        return true;
      }

      if (p.keepOpen && p.page !== page) {
        dispatch(OPEN_DRAWER_PAGE(p.page));
        return true;
      }
      
      return false;
    });
  }

  openActive = () => {
    const { dispatch, page, projectConfigurated, shouldConfigureFirst } = this.props;

    if (!projectConfigurated && shouldConfigureFirst) {
      return;
    }

    this.props.pages.some(p => {

      const currentPageData = this.getPage(page);

      if (currentPageData && currentPageData.invalid && currentPageData.page === page) {
        return true;
      }

      if (p.active && page !== p.page) {
        dispatch(OPEN_DRAWER_PAGE(p.page));
        return true;
      }
      
      return false;
    });
  }

  _clickedOnMapDesigningButton = (e) => {
    if (e &&
      e.target &&
      e.target.parentElement &&
      e.target.parentElement.parentElement &&
      e.target.parentElement.parentElement.parentElement &&
      e.target.parentElement.parentElement.parentElement.classList &&
      e.target.parentElement.parentElement.parentElement.classList.length) {
      return e.target.parentElement.parentElement.parentElement.classList[1] === 'design-on-map-button';
    }
  }

  _isClickOnUIComponent = (event: Event): boolean => {
    const target = event.target as HTMLElement;
    
    // Check for dropdown/select elements and their options
    if (target.closest('.MuiSelect-root') || 
        target.closest('.MuiMenu-root') || 
        target.closest('.MuiPopover-root') ||
        target.closest('.MuiPopper-root') ||
        target.closest('[role="listbox"]') ||
        target.closest('[role="option"]') ||
        target.closest('.dropdown') ||
        target.closest('.select') ||
        target.tagName === 'OPTION') {
      return true;
    }

    // Check for tooltips
    if (target.closest('.tooltip') || 
        target.closest('[role="tooltip"]') ||
        target.closest('.react-tooltip') ||
        target.closest('.MuiTooltip-tooltip') ||
        target.closest('.MuiTooltip-popper')) {
      return true;
    }

    // Check for modals and dialogs
    if (target.closest('.modal') || 
        target.closest('.dialog') || 
        target.closest('[role="dialog"]') ||
        target.closest('.MuiDialog-root')) {
      return true;
    }

    // // Check for date pickers and other complex UI components
    // if (target.closest('.date-picker') || 
    //     target.closest('.calendar') ||
    //     target.closest('.picker')) {
    //   return true;
    // }

    return false;
  }

  handleClickOutside = (event: Event) => {
    const { dispatch } = this.props;
    const target = event.target as Node;
    
    // Check if click is outside drawer components and should close
    if (
      this.drawerContent &&
      !this.drawerContent.contains(target) &&
      this.drawerList &&
      !this.drawerList.contains(target) &&
      this.props.page &&
      !this.props.keepOpen &&
      !this._clickedOnMapDesigningButton(event) &&
      !this.shouldKeepOpen() &&
      !this._isClickOnUIComponent(event)
    ) {
      // Add a small delay to prevent conflicts with other interactions
      setTimeout(() => {
        dispatch(OPEN_DRAWER_PAGE(null));
      }, 50);
    }
  }

  handleKeyDown = (event: KeyboardEvent) => {
    const { dispatch } = this.props;
    
    // Close drawer on Escape only when it is allowed to close (not keepOpen).
    if (event.key === 'Escape' && this.props.page && !this.props.keepOpen && !this.shouldKeepOpen()) {
      event.preventDefault();
      dispatch(OPEN_DRAWER_PAGE(null));
    }
  }

  shouldKeepOpen = () => {
    const { page } = this.props;
    const currentPage = this.props.pages.find(p => p.page === page);

    // Custom module selector has a lot of validation rules
    // therefore, we should keep the drawer open until all the requirements are met.
    if ((currentPage && currentPage.keepOpen) || !isModuleSelectorValid()) {
      return true;
    }

    return false;
  }

  setOpen = (page: PAGE) => {
    const { dispatch } = this.props;

    if (this.shouldKeepOpen()) {
      return;
    }

    if (page.page === this.props.page && !this.props.keepOpen) {
      // If keepOpen is false and same page clicked, toggle
      dispatch(OPEN_DRAWER_PAGE(null));
    } else {
      // Open the requested page
      dispatch(OPEN_DRAWER_PAGE(page.page));
    }
  }

  handleTabNavigation = (event: React.KeyboardEvent<HTMLButtonElement>, page: PAGE) => {
    if (!this.isContentTab(page)) {
      return;
    }

    const tabs = this.props.pages.filter(p => !p.disabled && this.isContentTab(p));
    const currentIndex = tabs.findIndex(p => p.page === page.page);
    let target: PAGE | undefined;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        target = tabs[(currentIndex + 1) % tabs.length];
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        target = tabs[(currentIndex - 1 + tabs.length) % tabs.length];
        break;
      case 'Home':
        target = tabs[0];
        break;
      case 'End':
        target = tabs[tabs.length - 1];
        break;
      default:
        return;
    }

    event.preventDefault();
    if (!target || this.shouldKeepOpen()) {
      return;
    }

    this.props.dispatch(OPEN_DRAWER_PAGE(target.page));
    requestAnimationFrame(() => {
      document.getElementById(`drawer-tab-${target.page}`)?.focus();
    });
  }


  list = () => {
    const { legacy, pages, mainClass } = this.props;
    const skipIconMotion = !!legacy || (mainClass || '').includes('engineering-full-width-drawer');
    const activeContentPage = pages.find(page => (
      !page.disabled && this.isContentTab(page) && page.page === this.props.page
    ));
    const firstContentPage = pages.find(page => !page.disabled && this.isContentTab(page));
    let bottomIconIndex = -1;
    return (
      <>
        {pages
          .filter(page => !page.disabled)
          .map((page) => {
            if(page.bottomIcon) bottomIconIndex++;
            const isActive = page.page === this.props.page;
            const isInvalid = page.invalid;
            const isContentTab = this.isContentTab(page);
            const isTabStop = isContentTab && (
              isActive || (!activeContentPage && page.page === firstContentPage?.page)
            );
            
            return (
            <CustomTooltip 
              key={page.page} 
              title={isActive ? '' : page.tooltip} 
              placement="right" 
              arrow
              enterDelay={300}
              isHtml={true}
            >
              <Button
                variant="text"
                disableRipple
                disableFocusRipple
                className={`drawer-icon ${page.page} ${
                  isActive ? 'active' : ''
                } ${isInvalid ? 'invalid' : ''} ${page.page}`}
                id={`drawer-tab-${page.page}`}
                aria-label={`${page.tooltip} ${isActive ? '(active)' : ''}`}
                aria-pressed={isContentTab ? isActive : undefined}
                aria-selected={isContentTab ? isActive : undefined}
                aria-controls={isContentTab ? `drawer-panel-${page.page}` : undefined}
                aria-invalid={isInvalid}
                role={isContentTab ? 'tab' : 'button'}
                tabIndex={isContentTab ? (isTabStop ? 0 : -1) : 0}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!skipIconMotion) {
                    const target = e.currentTarget;
                    target.classList.add('drawer-icon-clicked');
                    target.style.transform = 'scale(0.92)';
                    setTimeout(() => {
                      target.style.transform = '';
                      target.classList.remove('drawer-icon-clicked');
                    }, 200);
                  }
                  
                  if (page.onClick) {
                    page.onClick();
                  } else {
                    this.setOpen(page);
                  }
                }}
                onKeyDown={(e) => {
                  this.handleTabNavigation(e, page);
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (page.onClick) {
                      page.onClick();
                    } else {
                      this.setOpen(page);
                    }
                  }
                }}
                onMouseEnter={(e) => {
                  if (!skipIconMotion && !isActive) {
                    e.currentTarget.style.transform = 'translateY(-2px) scale(1.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!skipIconMotion && !isActive) {
                    e.currentTarget.style.transform = '';
                  }
                }}
                style={page.bottomIcon? { 
                  bottom: `${footerHeight + 10 + bottomIconIndex*50}px`,
                  position: 'absolute' as const,
                  ...(skipIconMotion ? {} : {
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                  }),
                }: skipIconMotion ? undefined : {
                  transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                {page.icon}
                {!legacy && isInvalid && (
                  <span className="drawer-icon-badge" aria-label="Requires attention">
                    !
                  </span>
                )}
              </Button>
            </CustomTooltip>
            );
          })}
      </>
    );
  }

  onSave = () => {
    const { dispatch, shouldConfigureFirst, verifiedFlyouts } = this.props;

    const { onSave } = this.props;
    if (onSave) {
      onSave();
    }

    if (this.shouldKeepOpen()) {
      return;
    }

    if (shouldConfigureFirst) {
      const { page } = this.props;
      const verifiedPages = verifiedFlyouts
        ? Object.keys(verifiedFlyouts).filter(p => verifiedFlyouts[p]?.checked)
        : [];
      const configuredPages = Array.from(new Set([
        ...this.state.configuredPages,
        ...verifiedPages,
        ...(page ? [page] : []),
      ]));
      const nextPage = this.getNextConfigurationPage(configuredPages);

      this.setState({
        configuredPages,
        pageInConfiguration: Boolean(nextPage),
      }, () => {
        dispatch(OPEN_DRAWER_PAGE(nextPage ? nextPage.page : null));
      });
      return;
    }

    dispatch(OPEN_DRAWER_PAGE(null));
  }

  content = () => {
    const { legacy, listClass, saveButtonLabel, page, productId } = this.props;
    const rm5Class = isRMFamily(productId) ? 'rm-family' : '';
    if (page === null) {
      return null;
    }

    const foundPage = this.props.pages.find(p => p.page === page);

    if (!foundPage || !foundPage.content) {
      return null;
    }

    return (
      <div
        ref={ref => (this.drawerContent = ref)}
        id={`drawer-panel-${page}`}
        className={`drawer-list drawer-list-enhanced ${legacy ? 'legacy-drawer' : ''} ${page}-content ${listClass || ''} ${rm5Class}`}
        role="tabpanel"
        aria-labelledby={`drawer-tab-${page}`}
        style={{
          maxHeight: this.contentHeight,
          overflowY: 'auto',
          overflowX: 'hidden'
        }}
      >
        <div className="drawer-content-wrapper">
          {foundPage.content}
          {this.props.saveButton && (
            <SaveChangesButton
              label={saveButtonLabel}
              onClick={this.onSave}
              page={foundPage.page}
              verifiedFlyouts={this.props.verifiedFlyouts}
            />
          )}
        </div>
      </div>
    );
  }

  render() {
    const { inactive, legacy } = this.props;
    return (
      <>
        <div
          // style={{ height: this.contentHeight }}
          ref={ref => (this.drawerList = ref)} // tslint:disable-next-line
          className={`drawer-component ${legacy ? 'legacy-drawer' : ''} ${ this.props.page === "accessories" ? this.props.page : '' } ${this.props.mainClass ? this.props.mainClass : '' } ${inactive == true ? 'inactive' : ''}`}
          role="tablist"
          aria-orientation="vertical"
          aria-label="Drawer navigation tabs"
        >
          {this.list()}
        </div>
        {this.content()}
      </>
    );
  }
}

function mapStateToProps(appState: appState) {
  return {
    page: appState.projectConfiguration.openedDrawerPage,
    projectConfigurated: appState.projectConfiguration.projectConfigurated,
    moduleSelector: appState.moduleSelector,
    verifiedFlyouts: appState.projectConfiguration.verifiedFlyouts,
    building_code: appState.projectConfiguration.projectEnvConfig.building_code
  };
}

export default connect(mapStateToProps)(Drawer);
